import {Id, toast, ToastOptions} from "react-toastify";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {addToastClassName} from "./toast-helpers/toast-classnames.helper";
import {IToastHandler, IToastDefinition, ToastCloseReason} from "./toaster.service.interface";
import {IReactionDisposer, when} from "mobx";

export class ToastHandler implements IToastHandler {
    public readonly toastId: Id;
    /**
     * reason can be:
     * ToastCloseReason.CloseButton if the toast was closed by the X button
     * undefined if was autoclosed by itself
     * or anything passed to handler.close (including undefined)
     */
    private closeReason: ToastCloseReason | string | undefined = undefined;
    constructor(private readonly services: IServiceFactoryExtended, private readonly options: IToastDefinition) {
        this.toastId = options.id ? options.id : this.services.tools.getGuid();
    }

    private _displayReactionDisposer: IReactionDisposer | null = null;
    show(): void {
        if(this.options.displayCondition) {
            this._displayReactionDisposer = when(() => this.options.displayCondition(),
                () => {
                    this._displayReactionDisposer = null;
                    this._displayToast();
                });
        } else {
            this._displayToast();
        }
    }

    private _displayToast(): void {
        const CloseIcon = this.services.theme.currentTheme.icons.closeIcon;
        let autoClose: number | false = false;
        if (this.options.toastOptions?.timeout && this.options.toastOptions.timeout.totalMilliseconds > 0) {
            autoClose = this.options.toastOptions.timeout.totalMilliseconds;
        }
        const toastOptions: ToastOptions<ToastHandler> = {
            // if autoClose is undefined it defaults to 5000ms, so instead of using autoClose I use timeout and if is missing (undefined) or null or 0 it defaults to false
            autoClose,
            hideProgressBar: this.options.toastOptions?.hideProgressBar,
            // If I don't use addToastClassName than this class name will override the one I set in ToastContainerDefaultBox
            className: addToastClassName(this.options.toastOptions?.className),
            // I need to pass a custom close Button that mimics the default one because react-toastify does not provide me with a mechanism to tell why the
            // toast was closed, so I need to patch this
            // If we'll ever use react-toastify theming add classes like Toastify__close-button--light to this
            closeButton: (this.options.toastOptions?.closeButton === false) ? false : () => <button aria-label="close" className="Toastify__close-button cy-toastify-close-button" type="button" onClick={() => {
                this.close(ToastCloseReason.CloseButton)
            }}><CloseIcon size={2} /></button>,
            toastId: this.toastId,
            data: this
        };
        toast<ToastHandler>(this.options.renderContent(this), toastOptions);
    }

    private _destroyReactions(): void {
        if(this._hideReactionDisposer) {
            this._hideReactionDisposer();
            this._hideReactionDisposer = null;
        }

        if(this._displayReactionDisposer) {
            this._displayReactionDisposer();
            this._displayReactionDisposer = null;
        }
    }

    close = (closeReason?: ToastCloseReason | string) => {
        if (closeReason) {
            this.closeReason = closeReason;
        }
        this._destroyReactions();
        toast.dismiss(this.toastId);
    }

    private _hideReactionDisposer: IReactionDisposer | null = null;
    onToastShown() {
        if (this.options.onToastShown) {
            this.options.onToastShown();
        }

        if(this.options.displayCondition) {
            this._hideReactionDisposer = when(() => !this.options.displayCondition(),
                () => {
                    this._hideReactionDisposer = null;
                    this.close();
                });
        }

    }
    onToastClosed() {
        this._destroyReactions();

        if (this.options.onToastClosed) {
            this.options.onToastClosed(this.closeReason);
        }
    }
}